import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import httpClient from "@/api/httpClient";
import queryClient from "@/api/queryClient";

interface OrganizationStatus {
  maintenanceMode: boolean;
}

// Fetched once per load, not polled: the httpClient interceptor re-checks it
// whenever a gateway call fails, so a maintenance toggle still surfaces.
const ORGANIZATION_STATUS_PATH = "/organization/status";
const ORGANIZATION_STATUS_KEY = ["organizationStatus"];
const MAX_STATUS_RETRIES = 3;
const RECHECK_THROTTLE_MS = 30_000;

let lastRecheckAt = 0;

const isTransientError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return true;
  if (!error.response) return true;
  return error.response.status >= 500;
};

const recheckMaintenanceStatus = () => {
  const now = Date.now();
  if (now - lastRecheckAt < RECHECK_THROTTLE_MS) return;
  lastRecheckAt = now;
  void queryClient.invalidateQueries({ queryKey: ORGANIZATION_STATUS_KEY });
};

export const registerMaintenanceRecheck = () => {
  httpClient.interceptors.response.use(undefined, (error) => {
    const isStatusCall = isAxiosError(error) && error.config?.url === ORGANIZATION_STATUS_PATH;
    if (!isStatusCall && isTransientError(error)) recheckMaintenanceStatus();
    return Promise.reject(error);
  });
};

export const useMaintenanceStatus = (): boolean => {
  const { data } = useQuery({
    queryKey: ORGANIZATION_STATUS_KEY,
    queryFn: () => httpClient.get<OrganizationStatus>(ORGANIZATION_STATUS_PATH),
    select: (res) => res.data?.maintenanceMode ?? false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => failureCount < MAX_STATUS_RETRIES && isTransientError(error),
  });

  return data ?? false;
};
